import React, { useState, useRef, useEffect } from 'react';
import { CARD_LIBRARY } from './Cards';

// ==========================================
// FUSION CASE — CSGO REEL
//
// Props:
//   cardName  – base card name being fused
//   cardCount – number of non-foil copies owned
//   onClose   – close modal (only before first spin)
//   onDeduct  – async fn() — deducts 10 cards at spin START, returns bool
//   onCollect – async fn(card) — adds reward to inventory on COLLECT
//
// Bug fixes:
//   • FUSE AGAIN now collects the previous result first, THEN deducts for
//     the next spin — you never lose a card.
//   • SKIP ANIMATION jumps straight to result.
// ==========================================

const RARITY_CONFIG = [
  { name: 'Special',   weight: 0,    color: '#ff00ff', bg: '#200020' },
  { name: 'Divine',    weight: 0.5,  color: '#ffffff', bg: '#0d0020' },
  { name: 'Mythic',    weight: 2,    color: '#ff0055', bg: '#1a0010' },
  { name: 'Legendary', weight: 8,    color: '#ffae00', bg: '#1a1000' },
  { name: 'Epic',      weight: 18,   color: '#a600ff', bg: '#0d0020' },
  { name: 'Rare',      weight: 30,   color: '#0077ff', bg: '#001525' },
  { name: 'Common',    weight: 41.5, color: '#555555', bg: '#111111' },
];

const RARITY_RANK = { Special: -1, Divine: 0, Mythic: 1, Legendary: 2, Epic: 3, Rare: 4, Common: 5 };

function buildRarityPool(fusedRarityName) {
  const fusedRank = RARITY_RANK[fusedRarityName] ?? 5;
  const eligible  = RARITY_CONFIG.filter(r => r.weight > 0 && RARITY_RANK[r.name] < fusedRank);
  const pool      = eligible.length > 0 ? eligible : RARITY_CONFIG.filter(r => r.weight > 0);
  const totalW    = pool.reduce((s, r) => s + r.weight, 0);
  return pool.map(r => ({ ...r, chance: (r.weight / totalW) * 100 }));
}

const CARD_W      = 110;
const TOTAL_CARDS = 48;
const WINNER_POS  = TOTAL_CARDS - 8;

function rollRarity(pool) {
  const roll = Math.random() * 100;
  let acc = 0;
  for (const r of pool) { acc += r.chance; if (roll < acc) return r; }
  return pool[pool.length - 1];
}

function pickCard(rarityName, cardPool) {
  const matching   = CARD_LIBRARY.filter(c => c.rarity === rarityName && c.pool === cardPool);
  const fallback   = CARD_LIBRARY.filter(c => c.pool === cardPool);
  const candidates = matching.length > 0 ? matching : fallback;
  return candidates[Math.floor(Math.random() * candidates.length)] || CARD_LIBRARY[0];
}

function easeOut(t) { return 1 - Math.pow(1 - t, 4); }

export default function FusionCase({ cardName, cardCount, onClose, onDeduct, onCollect }) {
  // phase: 'idle' | 'deducting' | 'spinning' | 'result' | 'collecting'
  const [phase,      setPhase]      = useState('idle');
  const [reelCards,  setReelCards]  = useState([]);
  const [winner,     setWinner]     = useState(null);
  const [translateX, setTranslateX] = useState(0);
  const [deductErr,  setDeductErr]  = useState('');
  const animRef    = useRef(null);
  const startRef   = useRef(null);
  const targetXRef = useRef(0);

  const canSpin = cardCount >= 10;

  const fusedCard   = CARD_LIBRARY.find(c => c.name === cardName);
  const fusedRarity = fusedCard?.rarity || 'Common';
  const fusedPool   = fusedCard?.pool   || 'standard';
  const rarityPool  = buildRarityPool(fusedRarity);

  function buildReel(winnerRarity, winnerCard) {
    const cards = [];
    for (let i = 0; i < TOTAL_CARDS; i++) {
      if (i === WINNER_POS) {
        cards.push({ rarity: winnerRarity, card: winnerCard, isWinner: true });
      } else {
        const r = rollRarity(rarityPool);
        cards.push({ rarity: r, card: pickCard(r.name, fusedPool), isWinner: false });
      }
    }
    return cards;
  }

  // ── Run a spin (deduct + animate) ───────────────────────────
  const runSpin = async () => {
    setDeductErr('');
    setPhase('deducting');
    const ok = await onDeduct();
    if (!ok) {
      setPhase('idle');
      setDeductErr('NOT ENOUGH NON-FOIL COPIES (NEED 10)');
      return;
    }

    const winnerRarity = rollRarity(rarityPool);
    const winnerCard   = pickCard(winnerRarity.name, fusedPool);
    const reel         = buildReel(winnerRarity, winnerCard);
    setReelCards(reel);
    setWinner({ rarity: winnerRarity, card: winnerCard });
    setPhase('spinning');

    const viewportWidth = Math.min(window.innerWidth - 80, 500);
    const center  = viewportWidth / 2 - CARD_W / 2;
    const targetX = -(WINNER_POS * CARD_W - center);
    const startX  = -(0 * CARD_W - center);
    targetXRef.current = targetX;
    startRef.current   = null;

    function animate(ts) {
      if (!startRef.current) startRef.current = ts;
      const elapsed  = ts - startRef.current;
      const progress = Math.min(elapsed / 3500, 1);
      setTranslateX(startX + (targetX - startX) * easeOut(progress));
      if (progress < 1) { animRef.current = requestAnimationFrame(animate); }
      else              { setTranslateX(targetX); setPhase('result'); }
    }
    animRef.current = requestAnimationFrame(animate);
  };

  // ── Skip: cancel animation, jump to result ───────────────────
  const handleSkip = () => {
    if (phase !== 'spinning') return;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setTranslateX(targetXRef.current);
    setPhase('result');
  };

  // ── Collect current result ───────────────────────────────────
  const handleCollect = async () => {
    if (!winner || phase !== 'result') return;
    setPhase('collecting');
    await onCollect(winner.card);
    // onCollect closes the modal (sets fusionTarget null in App.js)
  };

  // ── Fuse Again: collect first, then spin ─────────────────────
  // This fixes the bug where the previous winner was lost.
  const handleFuseAgain = async () => {
    if (!winner || phase !== 'result') return;
    if (cardCount < 10) return;
    setPhase('collecting');
    await onCollect(winner.card);   // save the card we already won
    setWinner(null);
    setReelCards([]);
    setDeductErr('');
    // Now run the next spin
    await runSpin();
  };

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  const rarityConf = name => RARITY_CONFIG.find(r => r.name === name) || RARITY_CONFIG[RARITY_CONFIG.length - 1];
  const isBusy = phase === 'spinning' || phase === 'deducting' || phase === 'collecting';

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget && !isBusy) onClose(); }}>
      <div style={s.modal}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.headerTitle}>FUSION CASE</div>
            <div style={s.headerSub}>
              {cardName} &nbsp;·&nbsp;
              <span style={{ color: canSpin ? '#00ff88' : '#ff4444' }}>{cardCount} non-foil copies</span>
              &nbsp;·&nbsp;
              <span style={{ color: '#555', fontSize: '0.6rem' }}>{fusedPool.toUpperCase()} POOL</span>
            </div>
          </div>
          {!isBusy && phase !== 'result' && (
            <button style={s.closeBtn} onClick={onClose}>✕</button>
          )}
        </div>

        {/* Odds */}
        <div style={s.oddsPills}>
          {rarityPool.map(r => (
            <div key={r.name} style={{ ...s.pill, background: r.bg, color: r.color, border: `1px solid ${r.color}44` }}>
              {r.name} {r.chance.toFixed(1)}%
            </div>
          ))}
        </div>

        {/* Error */}
        {deductErr && <div style={s.errorMsg}>{deductErr}</div>}

        {/* Reel */}
        <div style={s.reelOuter}>
          <div style={{ ...s.fade, left: 0,  background: 'linear-gradient(to right, #0f0f0f, transparent)' }} />
          <div style={{ ...s.fade, right: 0, background: 'linear-gradient(to left,  #0f0f0f, transparent)' }} />
          <div style={s.centerLine} />
          <div style={{ ...s.pointer, left:  0, borderLeft:  '14px solid #ff0055', borderRight: 'none' }} />
          <div style={{ ...s.pointer, right: 0, borderRight: '14px solid #ff0055', borderLeft:  'none' }} />
          <div style={s.reelWindow}>
            <div style={{ ...s.reelTrack, transform: `translateX(${translateX}px)` }}>
              {reelCards.length > 0 ? reelCards.map((item, i) => {
                const rc = rarityConf(item.rarity.name);
                return (
                  <div key={i} style={{
                    ...s.reelCard, background: rc.bg,
                    borderRight: `1px solid ${rc.color}22`,
                    outline: item.isWinner && phase === 'result' ? `2px solid ${rc.color}` : 'none',
                  }}>
                    <div style={{ ...s.reelRarity, color: rc.color }}>{item.rarity.name}</div>
                    <div style={{ ...s.reelName,   color: rc.color }}>{item.card.name}</div>
                    <div style={{ ...s.reelPower,  color: rc.color + '99' }}>+{item.card.power} PWR</div>
                  </div>
                );
              }) : Array.from({ length: 6 }).map((_, i) => {
                const rc = rarityPool[i % rarityPool.length];
                return (
                  <div key={i} style={{ ...s.reelCard, background: rc.bg, borderRight: `1px solid ${rc.color}22` }}>
                    <div style={{ ...s.reelRarity, color: rc.color }}>{rc.name}</div>
                    <div style={{ ...s.reelName,   color: rc.color }}>???</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Result box */}
        {phase === 'result' && winner && (
          <div style={{ ...s.resultBox, borderColor: rarityConf(winner.rarity.name).color + '88' }}>
            <div style={{ ...s.resultRarity, color: rarityConf(winner.rarity.name).color }}>
              {winner.rarity.name.toUpperCase()}
            </div>
            <div style={s.resultName}>{winner.card.name}</div>
            <div style={s.resultPower}>+{winner.card.power} PWR</div>
          </div>
        )}

        {/* ── Buttons ── */}

        {/* Idle / deducting */}
        {(phase === 'idle' || phase === 'deducting') && (
          <button
            style={{ ...s.spinBtn, ...(!canSpin || isBusy ? s.spinBtnDisabled : {}) }}
            onClick={runSpin}
            disabled={!canSpin || isBusy}
          >
            {phase === 'deducting'
              ? 'DEDUCTING...'
              : canSpin
                ? 'OPEN FUSION CASE (10 COPIES)'
                : `NEED ${10 - cardCount} MORE NON-FOIL COPIES`}
          </button>
        )}

        {/* Spinning — show Skip button */}
        {phase === 'spinning' && (
          <button style={{ ...s.spinBtn, background: '#333' }} onClick={handleSkip}>
            ⏭ SKIP ANIMATION
          </button>
        )}

        {/* Collecting */}
        {phase === 'collecting' && (
          <button style={{ ...s.spinBtn, ...s.spinBtnDisabled }} disabled>SAVING...</button>
        )}

        {/* Result — Collect + Fuse Again */}
        {phase === 'result' && (
          <div style={s.resultActions}>
            <button style={s.confirmBtn} onClick={handleCollect}>
              COLLECT REWARD
            </button>
            <button
              style={{ ...s.rerollBtn, ...(cardCount < 10 ? s.spinBtnDisabled : {}) }}
              onClick={handleFuseAgain}
              disabled={cardCount < 10}
              title={cardCount < 10 ? 'NOT ENOUGH COPIES' : 'COLLECTS THIS CARD + SPINS AGAIN (10 COPIES)'}
            >
              FUSE AGAIN {cardCount >= 10 ? '(10 COPIES)' : `(${cardCount}/10)`}
            </button>
          </div>
        )}

        <div style={s.costNote}>
          COSTS 10 NON-FOIL COPIES PER SPIN · {fusedPool.toUpperCase()} POOL · FUSE AGAIN COLLECTS FIRST
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, backdropFilter: 'blur(6px)' },
  modal:           { background: '#0f0f0f', border: '1px solid #222', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: '14px' },
  header:          { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  headerTitle:     { fontSize: '1rem', fontWeight: 900, letterSpacing: '2px', color: '#fff' },
  headerSub:       { fontSize: '0.7rem', color: '#555', marginTop: '4px', letterSpacing: '1px', textTransform: 'uppercase' },
  closeBtn:        { background: 'none', border: 'none', color: '#666', fontSize: '1.1rem', cursor: 'pointer' },
  oddsPills:       { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  pill:            { padding: '4px 9px', borderRadius: '6px', fontSize: '9px', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase' },
  errorMsg:        { background: 'rgba(255,68,0,0.1)', border: '1px solid rgba(255,68,0,0.4)', borderRadius: '6px', padding: '8px 12px', fontSize: '0.65rem', fontWeight: 900, color: '#ff4400', letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' },
  reelOuter:       { position: 'relative', borderRadius: '10px', overflow: 'hidden', border: '1px solid #222', background: '#0a0a0a' },
  reelWindow:      { overflow: 'hidden', height: '110px' },
  reelTrack:       { display: 'flex', willChange: 'transform' },
  reelCard:        { minWidth: `${CARD_W}px`, height: '110px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', flexShrink: 0 },
  reelRarity:      { fontSize: '7px',  fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase' },
  reelName:        { fontSize: '9px',  fontWeight: 900, textAlign: 'center', padding: '0 6px', lineHeight: 1.3 },
  reelPower:       { fontSize: '8px',  fontWeight: 900 },
  centerLine:      { position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', background: 'rgba(255,0,85,0.5)', transform: 'translateX(-50%)', zIndex: 5, pointerEvents: 'none' },
  pointer:         { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, zIndex: 10, borderTop: '10px solid transparent', borderBottom: '10px solid transparent' },
  fade:            { position: 'absolute', top: 0, bottom: 0, width: '60px', zIndex: 6, pointerEvents: 'none' },
  resultBox:       { background: '#111', border: '1px solid', borderRadius: '10px', padding: '16px', textAlign: 'center' },
  resultRarity:    { fontSize: '9px', fontWeight: 900, letterSpacing: '2px', marginBottom: '6px' },
  resultName:      { fontSize: '1.2rem', fontWeight: 900, color: '#fff', marginBottom: '4px' },
  resultPower:     { fontSize: '0.75rem', color: '#888', fontWeight: 900 },
  spinBtn:         { width: '100%', padding: '14px', background: '#ff0055', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', transition: 'background 0.2s' },
  spinBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  resultActions:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  confirmBtn:      { padding: '13px', background: '#00ff88', color: '#000', border: 'none', borderRadius: '8px', fontSize: '10px', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  rerollBtn:       { padding: '13px', background: 'transparent', color: '#ff0055', border: '1px solid #ff0055', borderRadius: '8px', fontSize: '10px', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  costNote:        { textAlign: 'center', fontSize: '8px', color: '#333', letterSpacing: '1px', textTransform: 'uppercase' },
};